import { useEffect, useMemo, useState } from 'react'
import type { KeyboardEvent, ReactNode } from 'react'

/**
 * El estado de una lista de opciones navegable con teclado: la búsqueda, la
 * opción activa y las teclas. Lo usan `Select` y `MultiSelect`; lo dibuja
 * `OptionList` (`listbox.tsx`).
 */

export interface ListOption {
  value: string
  label: ReactNode
  /** El texto plano de la opción: contra esto busca el buscador. */
  text: string
  disabled?: boolean
  /** Algo antes del texto: la espina de color de una empresa, un ícono. */
  leading?: ReactNode
}

/** Con más opciones que esto, el panel trae buscador. */
export const UMBRAL_DE_BUSQUEDA = 8

export function optionId(listId: string, index: number): string {
  return `${listId}-op-${String(index)}`
}

export interface ListNav {
  query: string
  setQuery: (value: string) => void
  visibles: ListOption[]
  active: number
  setActive: (index: number) => void
  /** Maneja flechas, Home/End y Enter/Espacio. Devuelve `true` si consumió la tecla. */
  onKey: (event: KeyboardEvent, pick: (option: ListOption) => void) => boolean
}

export function useListNav(
  options: readonly ListOption[],
  open: boolean,
  initial: number,
): ListNav {
  const [query, setQuery] = useState('')
  const [active, setActive] = useState(0)

  const visibles = useMemo(() => {
    const q = normalizar(query)
    return q ? options.filter((o) => normalizar(o.text).includes(q)) : [...options]
  }, [options, query])

  // Al abrir, la activa es la elegida; al cerrar, el buscador se vacía.
  useEffect(() => {
    if (open) setActive(Math.max(0, initial))
    else setQuery('')
  }, [open, initial])

  // Si la búsqueda achicó la lista, la activa no puede quedar afuera.
  useEffect(() => {
    setActive((actual) => Math.min(actual, Math.max(0, visibles.length - 1)))
  }, [visibles.length])

  const mover = (desde: number, paso: number): number => {
    if (visibles.length === 0) return 0
    let i = desde
    for (let n = 0; n < visibles.length; n++) {
      i = (i + paso + visibles.length) % visibles.length
      if (!visibles[i]?.disabled) return i
    }
    return desde
  }

  const onKey: ListNav['onKey'] = (event, pick) => {
    switch (event.key) {
      case 'ArrowDown':
        setActive(mover(active, 1))
        return true
      case 'ArrowUp':
        setActive(mover(active, -1))
        return true
      case 'Home':
        setActive(mover(-1, 1))
        return true
      case 'End':
        setActive(mover(visibles.length, -1))
        return true
      case 'Enter': {
        const opcion = visibles[active]
        if (opcion && !opcion.disabled) pick(opcion)
        return true
      }
      default:
        return false
    }
  }

  return { query, setQuery, visibles, active, setActive, onKey }
}

function normalizar(texto: string): string {
  return texto.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim()
}
